import { useTranslation } from "react-i18next";
import SvgIcon from "../SvgIcon";
import ToolTip from "../tooltip-portal";
import Sparkline from "./Sparkline";

/**
 * A KPI tile: title, tooltip, current value, "from previous" line, coloured
 * percent delta, and an optional sparkline slot. Pass `sparklineData` (+
 * optional `sparklineLabels` for the tooltip) to render a tiny recharts bar
 * chart; omit both for a plain KPI tile with no chart (Income's Overview
 * tab).
 *
 * `previous` is itself optional — pass `null`/`undefined` (not `0`) when no
 * prior-period baseline exists yet, and the comparison row is omitted
 * entirely rather than showing a fabricated "from 0".
 *
 * `formatValue` formats `current`/`previous` for display only — the percent
 * delta always math's off the raw numeric values.
 *
 * `caption` is an optional plain descriptive line rendered below the value
 * (e.g. "from 251 subscriptions") — unlike `tooltipText`, it's always
 * visible rather than hidden behind a hover icon, so pass it when the
 * context is worth surfacing at a glance. `highlight` flags the card as
 * needing attention (e.g. non-zero at-risk count), styling it accordingly.
 * `badge` is an optional node rendered next to the title (Income uses it
 * for the snapshot/period marker).
 */
const StatCard = ({
  title,
  badge,
  tooltipText,
  caption,
  highlight,
  current,
  previous,
  sparklineData,
  sparklineLabels,
  formatValue = (value) => value,
}) => {
  const { t } = useTranslation("coaching-portal");
  const hasPrevious = previous != null;
  const percentDiff =
    !hasPrevious || !previous
      ? 0
      : Number(((current / previous - 1) * 100).toFixed(2));
  return (
    <article className="stat-card" data-highlight={highlight || undefined}>
      <header>
        <div className="flex items-center gap-1">
          <p>
            <b>{title}</b>
          </p>
          {badge}
        </div>
        {tooltipText && (
          <div className="tooltip-section">
            <ToolTip body={[tooltipText]}>
              <SvgIcon src="/img/new-icons/question-circle.svg" />
            </ToolTip>
          </div>
        )}
      </header>
      <div className="body">
        <div className="stats">
          <div className="row">
            <h3>{formatValue(current)}</h3>
            {hasPrevious && (
              <p className="p1">
                {t("membershipList.fromPrevious", {
                  previous: formatValue(previous),
                })}
              </p>
            )}
          </div>
          {hasPrevious && (
            <div className="row">
              <p
                className={`s1 percent ${
                  percentDiff === 0 ? "" : percentDiff > 0 ? "up" : "down"
                }`}
              >
                {percentDiff !== 0 && (
                  <SvgIcon
                    src={
                      percentDiff === 0
                        ? ""
                        : percentDiff > 0
                          ? "/img/new-icons/green-arrow-up.svg"
                          : "/img/new-icons/red-arrow-down.svg"
                    }
                    noFilterOnLight={true}
                  />
                )}
                {percentDiff}%
              </p>
            </div>
          )}
        </div>
        {sparklineData && (
          <div className="graph-wrapper">
            <Sparkline data={sparklineData} labels={sparklineLabels} />
          </div>
        )}
      </div>
      {caption && <p className="stat-card__caption">{caption}</p>}
    </article>
  );
};

export default StatCard;
